import {useApi} from '#nk/composables/api';

// Pfad der Wegemeldungen-Übersichtsseite (CMS-Route, siehe public/routes.html).
export const WEGMELDUNGEN_PATH = '/wegemeldungen/';

// Projekt für den Outdooractive-Static-Map-Dienst (Karten-Thumbnail mit Marker).
const STATICMAP_PROJECT = 'oad-bad-peterstal-griesbach';

// Quellen, die nicht angezeigt werden sollen (Community-Meldungen, source.id
// 1011). So bleiben nur die offiziellen Meldungen des Tourismusbetriebs übrig –
// analog zur offiziellen Bedingungen-Seite.
const EXCLUDED_SOURCE_IDS = ['1011'];

export function staticMapUrl(id, size = 'small') {
	return `https://oad.outdooractive.com/api/staticmap?i=${id}&size=${size}&project=${STATICMAP_PROJECT}`;
}

// Bild-URL des Outdooractive-CDN (gleiches Muster wie bei den Touren).
export function wegmeldungImageUrl(id, width = 800, height = 450) {
	return `https://img.oastatic.com/img/${width}/${height}/${id}/.jpg`;
}

function stripHtml(html) {
	if (typeof html !== 'string') return '';
	// \r entfernen: der Browser normalisiert Zeilenumbrüche beim Parsen des
	// Server-HTML, sonst meldet Vue einen Hydration-Mismatch.
	return html
		.replace(/<[^>]*>/g, '')
		.replace(/\r/g, '')
		.trim();
}

function formatDate(value) {
	if (!value) return '';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return '';
	return d.toLocaleDateString('de-DE', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	});
}

// Aus dem Outdooractive-condition-Objekt ein flaches Anzeige-Objekt bauen.
// Feldnamen gemäß Data-API v1 (oois -> condition[0]), verifiziert am
// Test-Projekt api-dev-oa.
export function normalizeWegmeldung(cond) {
	if (!cond) return null;

	// Kategoriename ("Aktuelle Bedingungen") als Eyebrow-Label.
	const category = cond.category?.name || '';

	// Datum für das Badge: vorformatiert, sonst aus dem Prüfdatum ableiten.
	const date =
		cond.dateFormatted ||
		formatDate(
			cond.dayOfInspection || cond.dateFrom || cond.meta?.date?.created,
		);

	// Für die Sortierung nach Aktualität ein maschinenlesbares Datum verwenden.
	const rawDate =
		cond.dayOfInspection ||
		cond.dateFrom ||
		cond.meta?.date?.lastModified ||
		cond.meta?.date?.created ||
		'';

	const textHtml = typeof cond.longText === 'string' ? cond.longText : '';
	const riskHtml =
		typeof cond.riskDescription === 'string' ? cond.riskDescription : '';
	const text = stripHtml(textHtml) || stripHtml(riskHtml);

	const authorName = cond.meta?.authorFull?.name || cond.meta?.author || '';
	const sourceName = cond.meta?.source?.name || '';
	const author = authorName
		? `von ${authorName}${sourceName ? `, ${sourceName}` : ''}`
		: '';

	// Galeriebilder der Meldung (images.image[], verifiziert an Meldung
	// 810891197). Nicht-Galerie-Bilder werden ausgefiltert; das Quellen-Logo
	// steckt nur in meta.source.logo und taucht hier nicht auf.
	const images = (cond.images?.image || [])
		.filter(img => img && img.id && img.gallery !== false)
		.map(img => ({
			id: img.id,
			title: img.title || '',
			author: img.author || '',
		}));

	// Punkt-Geometrie ("lon,lat[,alt]") für die Kartendarstellung.
	let point = null;
	if (typeof cond.geometry === 'string' && cond.geometry.length) {
		const [lon, lat] = cond.geometry.split(' ')[0].split(',').map(Number);
		if (Number.isFinite(lat) && Number.isFinite(lon)) {
			point = {lat, lon};
		}
	}

	return {
		id: cond.id,
		sourceId: cond.meta?.source?.id || '',
		title: cond.title || '',
		category,
		// Gültigkeitsfenster der Meldung (beide Grenzen optional).
		// Bewusst als Rohwerte: ob die Meldung *jetzt* gilt, hängt von der
		// aktuellen Uhrzeit ab und darf nicht im normalisierten Objekt
		// festgeschrieben werden – sonst weicht ein serverseitig gerenderter
		// Wert bei der Hydration ab. Dafür gibt es istMeldungAktiv().
		dateFrom: cond.dateFrom || '',
		validTo: cond.validTo || '',
		date,
		rawDate,
		text,
		textHtml,
		riskHtml,
		author,
		images,
		point,
	};
}

// Gilt die Meldung zum aktuellen Zeitpunkt? Fehlende Grenzen gelten als
// offen (eine Meldung ohne validTo läuft nicht ab).
export function istMeldungAktiv(meldung, jetzt = new Date()) {
	if (!meldung) return false;

	const von = meldung.dateFrom ? new Date(meldung.dateFrom) : null;
	if (von && !Number.isNaN(von.getTime()) && von > jetzt) return false;

	const bis = meldung.validTo ? new Date(meldung.validTo) : null;
	if (bis && !Number.isNaN(bis.getTime()) && bis < jetzt) return false;

	return true;
}

export function useWegmeldungen() {
	const api = useApi();

	// Detaildaten mehrerer Meldungen laden. Die Outdooractive-oois-Schnittstelle
	// akzeptiert kommaseparierte IDs (ein Sammelabruf statt n Einzelabrufe);
	// schlägt der Sammelabruf fehl, werden die Meldungen einzeln geladen.
	async function fetchDetails(ids) {
		if (!ids.length) return [];

		try {
			const res = await api.get(
				`/api/outdooractive/bedingungen/${ids.join(',')}/`,
				{params: {lang: 'de'}},
			);
			const conditions = res.data?.condition;
			if (Array.isArray(conditions) && conditions.length) {
				return conditions;
			}
		} catch (error) {
			// Fallback auf Einzelabrufe unten.
		}

		const results = await Promise.all(
			ids.map(id =>
				api
					.get(`/api/outdooractive/bedingungen/${id}/`, {params: {lang: 'de'}})
					.then(r => r.data?.condition?.[0])
					.catch(() => null),
			),
		);
		return results.filter(Boolean);
	}

	// Aktuelle Wegemeldungen laden: IDs abrufen, Details nachladen,
	// Community-Quellen ausfiltern, nach Aktualität sortieren. Das Limit wird
	// erst nach dem Filtern angewendet, damit offizielle Meldungen nicht durch
	// ausgeblendete Community-Meldungen verdrängt werden.
	async function loadWegmeldungen({limit = 0} = {}) {
		const res = await api.get('/api/outdooractive/bedingungen/', {
			params: {lang: 'de'},
		});
		const ids = (res.data?.data || []).map(item => item.id);

		const meldungen = await loadWegmeldungenByIds(ids);

		return limit > 0 ? meldungen.slice(0, limit) : meldungen;
	}

	// Meldungen zu vorgegebenen IDs laden (z. B. die currentConditions einer
	// Tour) – gleiche Filterung und Sortierung wie die Übersicht.
	async function loadWegmeldungenByIds(ids) {
		const details = await fetchDetails((ids || []).filter(Boolean));

		return details
			.map(normalizeWegmeldung)
			.filter(Boolean)
			.filter(m => !EXCLUDED_SOURCE_IDS.includes(m.sourceId))
			.sort((a, b) => new Date(b.rawDate || 0) - new Date(a.rawDate || 0));
	}

	// Eine einzelne Wegemeldung laden (Detailseite).
	async function loadWegmeldung(id) {
		const res = await api.get(`/api/outdooractive/bedingungen/${id}/`, {
			params: {lang: 'de'},
		});
		return normalizeWegmeldung(res.data?.condition?.[0]);
	}

	return {loadWegmeldungen, loadWegmeldungenByIds, loadWegmeldung};
}
